import { useResolvedDesignSystemComponent } from 'ext-ui/design-system/DesignSystemChannel'
import GeneratedLatestPill, {
  PillRefinedProps,
} from 'ext-ui/design-system/generated/latest/components/Pill/Pill'
import GeneratedLivePill, {
  PillProps as GeneratedLivePillProps,
} from 'ext-ui/design-system/generated/live/components/Pill/Pill'
import React, { ReactElement } from 'react'

import PillLegacy, { PillProps as PillLegacyProps } from './PillLegacy'

type PillProps = PillLegacyProps &
  Pick<PillRefinedProps, 'iconLeft' | 'iconRight' | 'presentation'>

type PublicPillColor = NonNullable<PillProps['color']>
type GeneratedPillType = NonNullable<GeneratedLivePillProps['type']>
type PublicPillSize = NonNullable<PillProps['size']>
type GeneratedPillSize = NonNullable<GeneratedLivePillProps['size']>

const generatedTypeByPublicColor: Partial<
  Record<PublicPillColor, GeneratedPillType>
> = {
  highlight: 'highlight',
  new: 'aiNew',
  pending: 'pending',
  saas: 'saas',
  severe: 'severe',
}

const generatedSizeByPublicSize: Partial<
  Record<PublicPillSize, GeneratedPillSize>
> = {
  large: 'large',
  medium: 'medium',
  small: 'small',
}

function generatedTypeFor(color: PillProps['color']): GeneratedPillType | null {
  return color ? (generatedTypeByPublicColor[color] ?? null) : 'default'
}

function generatedSizeFor(size: PillProps['size']): GeneratedPillSize | null {
  return generatedSizeByPublicSize[size ?? 'medium'] ?? null
}

function fallbackChannelMarker(channel: 'live' | 'latest'): string {
  return channel === 'latest' ? 'latest-live-fallback' : 'live-legacy-fallback'
}

function renderDesignSystemPill(
  channel: 'live' | 'latest',
  props: PillProps,
): ReactElement {
  const { iconLeft, iconRight, presentation, ...legacyProps } = props
  const generatedType = generatedTypeFor(legacyProps.color)
  const generatedSize = generatedSizeFor(legacyProps.size)
  const shouldUseLegacyFallback =
    !generatedType || !generatedSize || Boolean(legacyProps.onClick)

  if (shouldUseLegacyFallback) {
    return (
      <PillLegacy
        {...legacyProps}
        data-design-system-channel={fallbackChannelMarker(channel)}
      >
        <>
          {iconLeft}
          {legacyProps.children}
          {iconRight}
        </>
      </PillLegacy>
    )
  }

  const { color: _color, size: _size, ...rest } = legacyProps
  const shouldRenderIcons =
    generatedSize !== 'small' ||
    (presentation !== undefined && presentation !== 'default')
  const generatedIconLeft = shouldRenderIcons ? iconLeft : undefined
  const generatedIconRight = shouldRenderIcons ? iconRight : undefined

  if (channel === 'latest') {
    return (
      <GeneratedLatestPill
        {...(rest as Omit<
          PillRefinedProps,
          'color' | 'iconLeft' | 'iconRight' | 'presentation' | 'size' | 'type'
        >)}
        data-design-system-channel={channel}
        iconLeft={generatedIconLeft}
        iconRight={generatedIconRight}
        presentation={presentation}
        size={generatedSize}
        type={generatedType}
      />
    )
  }

  return (
    <GeneratedLivePill
      {...(rest as Omit<
        GeneratedLivePillProps,
        'iconLeft' | 'iconRight' | 'size' | 'type'
      >)}
      data-design-system-channel={channel}
      iconLeft={generatedIconLeft}
      iconRight={generatedIconRight}
      size={generatedSize}
      type={generatedType}
    />
  )
}

function LivePill(props: PillProps): ReactElement {
  return renderDesignSystemPill('live', props)
}

function LatestPill(props: PillProps): ReactElement {
  return renderDesignSystemPill('latest', props)
}

function Pill(props: PillProps): ReactElement {
  const ResolvedPill = useResolvedDesignSystemComponent(LivePill, LatestPill)

  return <ResolvedPill {...props} />
}

export default Pill
export type { PillProps }
